import { Countries } from 'src/utils/Countries';
import { User, UserType } from './user';

type LineItemType =
  | 'discount'
  | 'product_shippable'
  | 'product_none_shippable'
  | 'total'
  | 'subtotal'
  | 'shipping'
  | 'sales_tax'
  | 'proration';

type PaymentProvider =
  | 'indiegogo'
  | 'stripe'
  | 'affirm'
  | 'flex'
  | 'unknown'
  | 'internal'
  | 'flex';
export type PaymentIntentStatus =
  | 'created'
  | 'requires_payment_method'
  | 'requires_confirmation'
  | 'requires_action'
  | 'processing'
  | 'requires_capture'
  | 'canceled'
  | 'succeeded'
  | 'failed'
  | 'refunded'
  | 'partially_refunded'
  | 'disputed_refunded';
export interface Metadata {
  id: string;
  key: string;
  value: string | number;
}

export type LineItem = {
  id: string;
  description: string;
  secondaryDescription?: string;
  currency: string;
  quantity: number;
  unitAmount: number;
  lineNumber: number;
  lineType?: LineItemType;
  refundAmount?: number;
  returnQuantity: number | null;
  refundQuantity: number | null;
  sku: string;
  taxRate?: number;
  priceId?: string;
  productId?: string;
};

export type Address = {
  firstName: string;
  lastName: string;
  cardHolderName: string;
  city: string;
  country: string;
  id: string;
  postal: string;
  province: string;
  street: string;
  unit: string;
  phone: string;
  vat: string;
};
export type Tracking = {
  id: string;
  courierName: string;
  serviceName: string;
  trackingNumber: string;
  trackingUrl: string;
};

export type WarrantyEvent = {
  id: string;
  warrantyCreated: {
    formatted: string;
  };
  warranteedUnitShippedDate: {
    formatted: string;
  };
  serialNo: string;
  reason?: string;
  subReason?: string;
  notes?: string;
  batchNo?: string;
  unitRetrieved?: boolean;
  haveSensorsBeenReplaced?: boolean;
  creditStatus?: string;
  headset: {
    id: string;
    mac: string;
  };
  order?: {
    id: string;
    confirmationCode: string;
  };
};

export type OrderData = {
  id: string;
  email: string;
  confirmationCode: string;
  paymentIntentId: string;
  fulfilmentStatus: string;
  fulfilmentId: number;
  fulfilmentAltId: number;
  paymentProvider: PaymentProvider;
  description: string;
  status: PaymentIntentStatus;
  amount: number;
  refundAmount: number;
  created: {
    formatted: string;
  };
  refundDate: {
    formatted: string;
  };
  shippedDate: {
    formatted: string;
  };
  lineItems: LineItem[];
  tracking?: Tracking;
  shippingAddress?: Address;
  billingAddress?: Address;
  utmCampaign: String;
  utmContent: String;
  utmMedium: String;
  utmSource: String;
  utmTerm: String;
  SEN_HEADSET_V1?: number;
  'SEN_HEADSET_V1.5'?: number;
  SEN_HEADSET_REFURBV1?: number;
  'SEN_HEADSET_OPENV1.5'?: number;
  sku3Quantity?: number;
  sku4Quantity?: number;
  sku5Quantity?: number;
  affiliateId?: string;
  refundReason: 'none' | 'other';
  refundNotes: string;
  couponCode: string;
  metadata: [Metadata];
  fulfilmentWarehouseCode?: string;
  user?: UserType;
  warrantyEvents: WarrantyEvent[];
};

export class Order {
  static skuMap = new Map<string, string>();

  id: string;
  email: string;
  confirmationCode: string;
  paymentIntentId: string;
  fulfilmentStatus: string;
  fulfilmentId: number;
  fulfilmentAltId: number;
  paymentProvider: PaymentProvider;
  description: string;
  status: PaymentIntentStatus;
  amount: number;
  refundAmount: number;
  created: {
    formatted: string;
  };
  refundDate: {
    formatted: string;
  };
  shippedDate: {
    formatted: string;
  };
  lineItems: LineItem[];
  tracking?: Tracking;

  shippingAddress?: Address;
  billingAddress?: Address;
  utmCampaign: String;
  utmContent: String;
  utmMedium: String;
  utmSource: String;
  utmTerm: String;
  shippingAddressValidationFlag?: boolean;

  affiliateId?: string;
  refundReason: 'none' | 'other';
  refundNotes: string;
  couponCode: string;
  metadata: [Metadata];
  hashedAudience?: any;
  SEN_HEADSET_V1: number = 0;
  'SEN_HEADSET_V1.5': number = 0;
  SEN_HEADSET_REFURBV1: number = 0;
  SEN_HEADSET_OPENV1: number = 0;
  'SEN_HEADSET_OPENV1.5': number = 0;
  sku3Quantity: number = 0;
  sku4Quantity: number = 0;
  sku5Quantity: number = 0;
  lineAmount: number = 0;
  shippingAmount: number = 0;
  tax1: number = 0;
  tax2: number = 0;
  tax3: number = 0;
  tax4: number = 0;
  tax5: number = 0;
  user?: User;
  fulfilmentWarehouseCode?: string;
  warrantyEvents: WarrantyEvent[];

  constructor(props: OrderData, user?: User) {
    this.user = user ?? (props.user ? new User(props.user) : undefined);
    this.id = props.id;
    this.email = props.email;
    this.confirmationCode = props.confirmationCode;
    this.paymentIntentId = props.paymentIntentId;
    this.fulfilmentStatus = props.fulfilmentStatus;
    this.fulfilmentId = props.fulfilmentId;
    this.fulfilmentAltId = props.fulfilmentAltId;
    this.paymentProvider = props.paymentProvider;
    this.description = props.description;
    this.status = props.status;
    this.amount = props.amount;
    this.refundAmount = props.refundAmount;
    this.created = props.created;
    this.refundDate = props.refundDate;
    this.shippedDate = props.shippedDate;
    this.lineItems = props.lineItems;
    this.tracking = props.tracking;
    this.shippingAddress = props.shippingAddress;
    this.billingAddress = props.billingAddress;
    this.utmCampaign = props.utmCampaign;
    this.utmContent = props.utmContent;
    this.utmMedium = props.utmMedium;
    this.utmSource = props.utmSource;
    this.couponCode = props.couponCode;
    this.affiliateId = props.affiliateId;
    this.refundReason = props.refundReason;
    this.refundNotes = props.refundNotes;
    this.metadata = props.metadata;
    this.fulfilmentWarehouseCode = props.fulfilmentWarehouseCode;
    this.warrantyEvents = props.warrantyEvents;

    Order.skuMap.set('SEN_HEADSET_V1', 'SEN_HEADSET_V1');
    Order.skuMap.set('SEN_HEADSET_V1.5', 'SEN_HEADSET_V1.5');
    Order.skuMap.set('SEN_HEADSET_OPENV1', 'SEN_HEADSET_OPENV1');
    Order.skuMap.set('SEN_HEADSET_OPENV1.5', 'SEN_HEADSET_OPENV1.5');
    Order.skuMap.set('SEN_HEADSET_REFURBV1', 'SEN_HEADSET_REFURBV1');
    Order.skuMap.set('SNSR_KIT1', 'SNSR_KIT1');

    this.lineItems = [...props.lineItems].sort((a, b) =>
      (a.lineType + a.description).localeCompare(b.lineType + b.description)
    );
    let taxCount = 1;
    this.lineItems.forEach((l) => {
      if (
        l.lineType === 'product_shippable' ||
        l.lineType === 'product_none_shippable'
      ) {
        const prop = !Order.skuMap.has(l.sku)
          ? `sku${Order.skuMap.size + 1}Quantity`
          : Order.skuMap.get(l.sku);
        this[prop] = l.quantity - l.refundQuantity - l.returnQuantity;
        Order.skuMap.set(l.sku, prop);
        this.lineAmount += l.unitAmount * (l.quantity - l.refundQuantity);
      }
      if (l.lineType === 'discount') {
        this.lineAmount =
          l.unitAmount < 0
            ? l.unitAmount + this.lineAmount
            : this.lineAmount - l.unitAmount;
      }
      if (l.lineType === 'shipping') {
        this.shippingAmount += l.unitAmount;
      }
      if (l.lineType === 'sales_tax') {
        this['tax' + taxCount] = l.unitAmount;
        taxCount++;
      }
    });

    this.shippingAddressValidationFlag = false;
    const shippableLine = this.lineItems.find(
      (l) => l.lineType === 'product_shippable'
    );

    if (this.shippingAddress && shippableLine) {
      const country = this.shippingAddress?.country;

      const countryInfo = country
        ? Countries.get(capitalizeFirstLetter(country))
        : null;

      const province = this.shippingAddress?.province;
      const postal = this.shippingAddress?.postal;
      if (!country || (!province && !countryInfo?.isEU) || !postal) {
        this.shippingAddressValidationFlag = true;
      } else {
        if (
          countryInfo &&
          countryInfo.postalRegex &&
          countryInfo.postalRegex !== ''
        ) {
          const ex = new RegExp(countryInfo.postalRegex);
          this.shippingAddressValidationFlag = !ex.test(postal);
        }
      }
    }
  }

  async getHashedAudience(): Promise<any> {
    if (this.hashedAudience) {
      return this.hashedAudience;
    }

    const shippableLine = this.lineItems.find(
      (l) => l.lineType === 'product_shippable'
    );

    let hashedAudience: any = {};
    if (shippableLine && this.shippingAddress) {
      const country = this.shippingAddress?.country;

      const countryInfo = country
        ? Countries.get(capitalizeFirstLetter(country))
        : null;

      hashedAudience.country = countryInfo?.code;
      hashedAudience.phone = await setAndBuildPhoneFormat(
        country,
        this.shippingAddress?.phone
      );

      hashedAudience.firstName = await digestMessage(
        this.shippingAddress?.firstName.trim().toLowerCase()
      );
      hashedAudience.lastName = await digestMessage(
        this.shippingAddress?.lastName.trim().toLowerCase()
      );
      hashedAudience.email = await digestMessage(
        this.email.trim().toLowerCase()
      );
    }
    this.hashedAudience = hashedAudience;
    return this.hashedAudience;
  }
}
const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const digestMessage = async (message: string): Promise<string> => {
  const msgUint8 = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return hashHex;
};

const setAndBuildPhoneFormat = (country?: string, phone?: string) => {
  let ph = phone.trim(); // clean & hash
  ph = ph.replace(/\D/g, ''); //remove non numbers

  if (country && phone) {
    const countryCodeList = Countries.get(
      capitalizeFirstLetter(country)
    )?.countryCallingCodes;
    if (countryCodeList && countryCodeList.length > 0) {
      const countryCode = countryCodeList[0].split(' ')[0];
      const countryCodeNum = countryCode.replace('+', '');

      if (!ph.startsWith(countryCodeNum)) {
        ph = countryCodeNum + ph;
      }
    }
  }
  return digestMessage('+' + ph);
};
